import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import { MERCURY_LAYER, TAB_FONT_SIZE, TAB_INDICATOR_HEIGHT, TAB_PADDING_X, TAB_PADDING_Y } from 'HubStyleTokens/sizes';
import { TABS_ACTIVATE_TRANSITION_TIMING } from 'HubStyleTokens/times';
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import styled, { css } from 'styled-components';
import { callIfPossible } from '../core/Functions';
import SyntheticEvent from '../core/SyntheticEvent';
import UIIcon from '../icon/UIIcon';
import UILink from '../link/UILink';
import { PLACEMENTS } from '../tooltip/PlacementConstants';
import UITooltip from '../tooltip/UITooltip';
import { hidden } from '../utils/propTypes/decorators';
import { getIconNamePropType } from '../utils/propTypes/iconName';
import { wrapPropTypes } from '../utils/propTypes/wrapPropTypes';
import { FONT_FAMILIES, setFontSize, setFontSmoothing, setInputMetrics, setUiTransition, toPx } from '../utils/Styles';
import { BORDER_COLOR, DISABLED_COLOR, ENCLOSED_BACKGROUND_COLOR, ENCLOSED_SHADED_BACKGROUND_COLOR, FOREGROUND_COLOR, FOREGROUND_FOCUS_COLOR } from './TabConstants';
import { getSelectedTabBackgroundColor, isContained } from './utils/tabUtils';
const TabIndicator = styled.span.withConfig({
  displayName: "UITab__TabIndicator",
  componentId: "sc-1gjok9k-0"
})(["", ";background-color:currentColor;border-radius:24px;bottom:", ";height:", ";left:0;opacity:", ";position:absolute;width:100%;z-index:", ";"], setUiTransition(), toPx(parseInt(TAB_INDICATOR_HEIGHT, 10) / 2 * -1), TAB_INDICATOR_HEIGHT, ({
  tabIsActive
}) => tabIsActive ? '1 !important' : '0', ({
  tabIsActive
}) => tabIsActive ? MERCURY_LAYER : null);
const TabIcon = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["tabIsActive"]);

  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest));
}).withConfig({
  displayName: "UITab__TabIcon",
  componentId: "sc-1gjok9k-1"
})(["&&&&{color:", ";}"], ({
  tabIsActive
}) => tabIsActive ? 'currentColor' : null);
TabIcon.displayName = 'StyledTabIcon';

const handleFontFamily = isActive => {
  return isActive ? css(["", ";&:hover{", ";}"], FONT_FAMILIES.medium, FONT_FAMILIES.medium) : FONT_FAMILIES.default;
};

const handleHoverColor = isActive => {
  return !isActive ? css(["&:hover{color:", ";}"], FOREGROUND_FOCUS_COLOR) : null;
};

const handleDisabledOnlyStyles = isDisabled => {
  return isDisabled ? css(["cursor:not-allowed;", "{color:", ";}"], TabIcon, DISABLED_COLOR) : null;
};

const getBackgroundColor = (use, active) => {
  if (active) {
    return getSelectedTabBackgroundColor(use);
  }

  return use === 'enclosed-shaded' ? ENCLOSED_SHADED_BACKGROUND_COLOR : ENCLOSED_BACKGROUND_COLOR;
};

const handleContainedStyles = (use, active) => isContained(use) ? css(["transition:background-color ", " cubic-bezier(0.25,0.1,0.25,1);background-color:", ";border-left:1px solid ", ";&:first-child{border-left:0;border-top-left-radius:inherit;}&:last-of-type{border-top-right-radius:inherit;}", "{display:none;}", ";"], TABS_ACTIVATE_TRANSITION_TIMING, getBackgroundColor(use, active), BORDER_COLOR, TabIndicator, active ? css(["border-bottom-color:", ";&::after{background:", " !important;bottom:-1px;content:' ';height:1px;left:0;position:absolute;width:100%;}"], getSelectedTabBackgroundColor(use), getSelectedTabBackgroundColor(use)) : null) : null;

const StyledLink = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, ["active", "fill", "use"]);

  return /*#__PURE__*/_jsx(UILink, Object.assign({
    use: "unstyled"
  }, rest));
}).withConfig({
  displayName: "UITab__StyledLink",
  componentId: "sc-1gjok9k-2"
})(["&&{", ";", ";", ";", ";color:", ";position:relative;transition-property:", ";white-space:nowrap;&:focus{text-decoration:none;z-index:", ";}", ";", ";", ";", ";&.sr-only-focusable{align-items:center;display:inline-flex;}}"], setFontSmoothing('auto'), ({
  active
}) => handleFontFamily(active), setFontSize(TAB_FONT_SIZE), setInputMetrics(TAB_PADDING_Y, TAB_PADDING_X, TAB_PADDING_X, TAB_PADDING_Y, false), ({
  disabled
}) => disabled ? `${DISABLED_COLOR} !important` : FOREGROUND_COLOR, ({
  use
}) => isContained(use) ? 'background-color, color' : 'color', MERCURY_LAYER, ({
  active
}) => handleHoverColor(active), ({
  disabled
}) => handleDisabledOnlyStyles(disabled), ({
  fill
}) => fill ? css(["text-align:center;width:100%;"]) : null, ({
  use,
  active
}) => use && handleContainedStyles(use, active));
export default class UITab extends PureComponent {
  constructor(...args) {
    super(...args);

    this.handleClick = evt => {
      const {
        onClick,
        tabId
      } = this.props;
      callIfPossible(onClick, SyntheticEvent(tabId, evt));
    };
  }

  render() {
    const _this$props = this.props,
          {
      active,
      className,
      disabled,
      icon,
      Link = StyledLink,
      title,
      tabId,
      tooltip,
      tooltipPlacement,
      tooltipProps
    } = _this$props,
          rest = _objectWithoutPropertiesLoose(_this$props, ["active", "className", "disabled", "icon", "Link", "onClick", "title", "tabId", "tooltip", "tooltipPlacement", "tooltipProps"]);

    const renderedTabContents = /*#__PURE__*/_jsxs(Link, Object.assign({}, rest, {
      active: active,
      className: classNames(className, 'private-tab', active && 'private-tab--active'),
      disabled: disabled,
      "data-tab-id": tabId,
      "data-tab-selected": active,
      onClick: this.handleClick,
      children: [icon ? /*#__PURE__*/_jsx(TabIcon, {
        tabIsActive: active,
        className: "private-tab__icon",
        name: icon,
        size: "xxs"
      }) : null, title, /*#__PURE__*/_jsx(TabIndicator, {
        tabIsActive: active,
        className: "private-tab__indicator"
      })]
    }));

    if (!tooltip) return renderedTabContents;
    return /*#__PURE__*/_jsx(UITooltip, Object.assign({
      placement: tooltipPlacement,
      title: tooltip
    }, tooltipProps, {
      children: renderedTabContents
    }));
  }

}
UITab.displayName = 'UITab';
UITab.propTypes = Object.assign({}, wrapPropTypes(UILink, ['use', 'underline']), {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  // @ts-expect-error UILink has propTypes
  href: UILink.propTypes.href,
  icon: getIconNamePropType(),
  Link: hidden(PropTypes.elementType.isRequired),
  tabId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  title: PropTypes.node.isRequired,
  tooltip: PropTypes.node,
  tooltipProps: PropTypes.object,
  tooltipPlacement: PropTypes.oneOf(PLACEMENTS)
});
UITab.defaultProps = {
  Link: StyledLink,
  onClick: emptyFunction
};